import { useCallback, useEffect, useMemo, useRef } from 'react'

import { useTranslation } from 'react-i18next'

import { useAuthStore } from 'src/app/auth/store/auth-store'
import { Toast, toastTypes } from 'src/app/core/services/toast'
import { useListUrlState } from 'src/app/home/hooks/use-list-url-state'
import { usePortfolioNewsCompaniesQuery } from 'src/app/home/repositories/use-portfolio-news-companies-query'
import {
  usePortfolioNewsQuery,
  type IPortfolioNewsQueryResult,
} from 'src/app/home/repositories/use-portfolio-news-query'

import type {
  IPortfolioNewsArticleEntity,
  IUsePortfolioNewsReturn,
  PortfolioNewsCompanyFilter,
} from '../types'

const PAGE_SIZE = 6

export const usePortfolioNews = (): IUsePortfolioNewsReturn => {
  const { t } = useTranslation()
  const isAuthenticated = useAuthStore(state => state.isAuthenticated())

  const {
    page: currentPage,
    searchQuery,
    debouncedSearchQuery,
    filters,
    setPage: setCurrentPage,
    setSearchQuery,
    setFilter,
  } = useListUrlState({
    searchEnabled: true,
    filters: [{ key: 'company', default: 'all' }],
  })

  const companyFilter = filters.company as PortfolioNewsCompanyFilter
  const setCompanyFilter = useCallback(
    (value: PortfolioNewsCompanyFilter): void => setFilter('company', value),
    [setFilter],
  )

  const listQuery = usePortfolioNewsQuery({
    page: currentPage,
    pageSize: PAGE_SIZE,
    search: debouncedSearchQuery,
    company: companyFilter === 'all' ? '' : companyFilter,
    enabled: isAuthenticated,
    silentErrorToast: true,
  })

  const companiesQuery = usePortfolioNewsCompaniesQuery({
    enabled: isAuthenticated,
  })

  const lastSuccessfulData = useRef<IPortfolioNewsQueryResult | undefined>(
    undefined,
  )

  if (listQuery.data) {
    lastSuccessfulData.current = listQuery.data
  }

  const isPaginationError =
    listQuery.isError && lastSuccessfulData.current !== undefined

  useEffect((): void => {
    if (!listQuery.isError) {
      return
    }

    Toast.notify({
      type: toastTypes.ERROR,
      title: t('portfolioNews.detail.loadErrorToastTitle'),
      message: t('portfolioNews.detail.loadErrorToastMessage'),
      options: { toastId: 'portfolio-news-list-fetch-error' },
    })
  }, [listQuery.isError, t])

  const displayData = listQuery.data ?? lastSuccessfulData.current

  const pageItems = useMemo((): IPortfolioNewsArticleEntity[] => {
    return displayData?.items ?? []
  }, [displayData?.items])

  const total = displayData?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const companies = companiesQuery.data ?? []

  const isEmpty =
    listQuery.isSuccess && (listQuery.data?.items?.length ?? 0) === 0

  return {
    isLoading: listQuery.isLoading,
    isError: listQuery.isError,
    isPaginationError,
    isEmpty,
    searchQuery,
    companyFilter,
    currentPage,
    totalPages,
    pageItems,
    companies,
    setSearchQuery,
    setCompanyFilter,
    setCurrentPage,
  }
}
